/**
 * Рисование фигур разметки.
 *
 * Чистые функции над холстом: сюда приходят уже пересчитанные экранные
 * точки, а решения о том, что и когда рисовать, остаются снаружи.
 *
 * Разделено по семействам, а не свалено в один switch: у линий, каналов,
 * Фибоначчи и подписей общего мало, зато внутри семейства почти всё
 * общее.
 */

import { withAlpha } from './color.js';
import { poseOf, stickerBounds, stickerHandles } from './sticker-pose.js';
import { spotOn, spotOnLongest, type CaptionSpot } from './captions.js';
import { channelHandles, channelOf, channelOutline } from './channels.js';
import { regressionFit, trendWords } from './regression.js';
import { shapeOptionsOf } from './shape-options.js';
import { drawAnchoredProfile, drawAnchoredVwap, drawRangeProfile } from './volume-draw.js';
import { onCanvasLeft, onCanvasTop } from './geometry.js';
import type { Candle } from '../state/store.js';
import { LINE_STYLES, type Drawing, type DrawingPoint } from './types.js';
import { activeLevels, fibOptionsOf, fibSpan, levelLabel, type FibOptions } from './fib.js';
import { drawFiboTrade } from './fibo-trade-draw.js';
import { pointsNeeded } from './kinds.js';
import { PATTERN_KINDS, drawPattern } from './patterns.js';
import { LOOK_KINDS, drawLook } from './looks.js';
import { stickerImage, stickerOf } from './stickers.js';
import { fontOf, textBox, textStyleOf, wrapText } from './text-style.js';
import {
  computePosition,
  money,
  positionOptionsOf,
  quantityText,
  showsNumbers,
} from './position.js';

export interface Screen {
  readonly x: number;
  readonly y: number;
}

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface DrawScope {
  readonly ctx: CanvasRenderingContext2D;
  readonly width: number;
  readonly height: number;
  /** Шаг цены инструмента — расстояния в тиках считаются по нему. */
  readonly tickSize: number;
  /** Знаков в объёме по умолчанию. */
  readonly qtyPrecision: number;
  /** Фигура под курсором: у прогноза позиции подписи всплывают по наведению. */
  readonly hoveredId: string | null;
  /** Фигура в работе: её подписи видны, пока её тянут. */
  readonly activeId: string | null;
  /**
   * Фигура, которую растягивают за опору или ещё рисуют.
   *
   * Регрессия в это время показывает простую линию, а канал считает,
   * когда отпустили: пересчёт на каждое движение мыши мельтешил бы.
   */
  readonly stretchingId?: string | null;
  /**
   * Куда рисовальщик складывает занятое фигурой место.
   *
   * Подпись рисуется со смещением от точки привязки и занимает столько,
   * сколько занимает текст в текущем шрифте. Снаружи этого не вычислить,
   * поэтому границы записывает тот, кто рисует, а попадание курсором
   * считается уже по ним.
   */
  readonly bounds?: Map<string, Rect>;
  /**
   * Где у фигуры опоры, если не там, где лежат её точки.
   *
   * Некоторые фигуры берут от точки только цену или только время: у
   * параллельного канала третья точка задаёт лишь сдвиг второй линии, у
   * стрелки вверх вторая — лишь длину. Кольцо, нарисованное в самой
   * точке, висело там, где его отпустили, — хоть на другом конце экрана.
   * Такие фигуры записывают сюда, где их опоры на самом деле: на линии,
   * на хвосте, на краю. Рисование опор и попадание по ним читают отсюда.
   */
  readonly anchors?: Map<string, ReadonlyArray<Screen | null>>;
  /**
   * Где подписывать линию: середина и наклон (см. captions.ts).
   *
   * Отмечает рисовальщик — только он знает, где у канала верхняя граница
   * и куда на самом деле ушёл луч. Надпись рисует слой.
   */
  readonly captions?: Map<string, CaptionSpot>;
  /** Точка модели → экран. `null` — точка вне шкалы. */
  readonly screen: (point: DrawingPoint) => Screen | null;
  /** Номер бара по времени — для «Δ баров». */
  readonly logical: (time: number) => number;
  readonly candles: readonly Candle[];
}

const FONT = '500 11px Onest, system-ui, sans-serif';

export function drawShape(scope: DrawScope, shape: Drawing): void {
  const { ctx } = scope;
  const points = shape.points.map((point) => scope.screen(point));
  const a = points[0];
  if (!a) return;
  const b = points[1] ?? null;
  const c = points[2] ?? null;

  ctx.save();
  ctx.strokeStyle = shape.color;
  ctx.fillStyle = shape.color;
  ctx.lineWidth = shape.width;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.font = FONT;
  ctx.textBaseline = 'middle';
  // Стиль линии применяется ко всей фигуре разом: пунктирный
  // прямоугольник должен быть пунктирным целиком.
  ctx.setLineDash(dashOf(shape));

  // Незаконченная фигура рисуется наброском по уже поставленным точкам.
  // Канал, треугольник и расширение Фибоначчи требуют трёх точек, и без
  // наброска они не показывались вовсе, пока не поставлена последняя:
  // со стороны это выглядело так, будто инструмент не тянется, а
  // возникает по последнему клику.
  if (shape.points.length < pointsNeeded(shape.kind)) {
    sketch(ctx, points);
    ctx.restore();
    return;
  }

  // Стрелки и заметки — фирменный облик, см. `looks.ts`.
  if (LOOK_KINDS.has(shape.kind)) {
    drawLook(scope, shape, points);
    ctx.restore();
    return;
  }

  // Инструменты разбора — паттерны, волны, вилы, Ганн — в своём файле.
  if (PATTERN_KINDS.has(shape.kind)) {
    drawPattern(scope, shape, points.filter((point): point is Screen => point !== null));
    ctx.restore();
    return;
  }

  /*
   * Линии и каналы светятся мягким ореолом своего цвета: на тёмном графике
   * они читаются как неон, а не как карандаш. Заливки у каналов лёгкие,
   * и ореол им не мешает.
   */
  if (GLOW_KINDS.has(shape.kind)) {
    ctx.shadowColor = withAlpha(shape.color, 0.45);
    ctx.shadowBlur = 5 + shape.width * 1.5;
  }

  switch (shape.kind) {
    // --- линии ------------------------------------------------------------
    case 'trend':
      if (b) {
        line(ctx, a, b);
        caption(scope, shape, spotOn(a, b));
      }
      break;

    case 'ray':
      if (b) {
        line(ctx, a, project(a, b, scope, 'forward'));
        caption(scope, shape, spotOn(a, b));
      }
      break;

    case 'extended':
      if (b) {
        line(ctx, project(b, a, scope, 'forward'), project(a, b, scope, 'forward'));
        caption(scope, shape, spotOn(a, b));
      }
      break;

    case 'horizontal':
      line(ctx, { x: 0, y: a.y }, { x: scope.width, y: a.y });
      // Горизонталь бесконечна — подпись посередине видимой части.
      caption(scope, shape, { x: scope.width / 2, y: a.y, angle: 0, place: 'above' });
      break;

    case 'horizontal-ray':
      line(ctx, a, { x: scope.width, y: a.y });
      caption(scope, shape, spotOn(a, { x: Math.max(a.x + 80, scope.width), y: a.y }, 0.5));
      break;

    case 'vertical':
      line(ctx, { x: a.x, y: 0 }, { x: a.x, y: scope.height });
      // Вдоль вертикали, снизу вверх — как подписывают ось.
      caption(scope, shape, { x: a.x, y: scope.height / 2, angle: -Math.PI / 2, place: 'above' });
      break;

    case 'cross':
      line(ctx, { x: 0, y: a.y }, { x: scope.width, y: a.y });
      line(ctx, { x: a.x, y: 0 }, { x: a.x, y: scope.height });
      break;

    case 'info-line':
      if (b) {
        line(ctx, a, b);
        label(ctx, midpoint(a, b), infoText(scope, shape), shape.color);
        // Середину занимают числа — своя подпись ближе к началу.
        caption(scope, shape, spotOn(a, b, 0.25));
      }
      break;

    case 'trend-angle':
      if (b) {
        line(ctx, a, b);
        // Горизонталь-основание: угол считается от неё, и без неё
        // подпись выглядела бы взятой из воздуха.
        dashed(ctx, a, { x: b.x, y: a.y });
        const angle = (Math.atan2(a.y - b.y, b.x - a.x) * 180) / Math.PI;
        label(ctx, b, `${angle.toFixed(1)}°`, shape.color);
        caption(scope, shape, spotOn(a, b));
      }
      break;

    // --- каналы -----------------------------------------------------------
    /*
     * Каналы — одна геометрия на рисование и на попадание курсором
     * (см. channels.ts): кружок, который виден, за тот и тянется.
     */
    case 'parallel-channel':
    case 'flat-channel':
    case 'disjoint-channel': {
      const channel = channelOf(shape.kind, points);
      if (!channel) break;
      const [a1, b1] = channel.first;
      const [a2, b2] = channel.second;
      fill(ctx, channelOutline(channel));
      line(ctx, a1, b1);
      line(ctx, a2, b2);
      // У параллельного канала середина пунктиром — к ней цена чаще всего и возвращается.
      if (shape.kind === 'parallel-channel') {
        dashed(ctx, { x: a1.x, y: (a1.y + a2.y) / 2 }, { x: b1.x, y: (b1.y + b2.y) / 2 });
      }
      scope.anchors?.set(shape.id, channelHandles(shape.kind, channel));
      // Подпись — над верхней границей: внутри канала она легла бы на свечи.
      const upper = (a1.y + b1.y) / 2 <= (a2.y + b2.y) / 2 ? channel.first : channel.second;
      // Середины линий заняты кружками: подпись — на трети пути, чтобы не спорить с ними.
      caption(scope, shape, spotOn(upper[0], upper[1], shape.kind === 'parallel-channel' ? 0.3 : 0.5));
      break;
    }

    case 'regression':
      if (b) regression(scope, shape, a, b);
      break;

    // --- на основе объёма -------------------------------------------------
    case 'anchored-vwap':
      drawAnchoredVwap(scope, shape, a);
      break;
    case 'volume-profile-range':
      if (b) drawRangeProfile(scope, shape, a, b);
      break;
    case 'volume-profile-anchored':
      drawAnchoredProfile(scope, shape, a);
      break;

    // --- Фибоначчи --------------------------------------------------------
    case 'fib-retracement': {
      const options = fibOptionsOf(shape.kind, shape.fib);
      if (b && options) {
        if (options.trendLine) dashed(ctx, a, b);
        fibLevels(scope, shape, options, a, b, shape.points[0]!.price, shape.points[1]!.price);
        // «Фибо-пульт»: вход, стоп и цель сделки по этой сетке.
        drawFiboTrade(scope, shape, a, b);
      }
      break;
    }

    case 'fib-extension': {
      const options = fibOptionsOf(shape.kind, shape.fib);
      if (b && c && options) {
        if (options.trendLine) {
          line(ctx, a, b);
          dashed(ctx, b, c);
        }
        // Отсчёт ведётся от третьей точки на длину первого хода:
        // в этом и состоит «расширение по тренду».
        const move = shape.points[1]!.price - shape.points[0]!.price;
        const from = shape.points[2]!.price;
        fibLevels(scope, shape, options, { x: c.x, y: c.y }, { x: b.x, y: c.y }, from, from + move);
      }
      break;
    }

    case 'fib-fan': {
      const options = fibOptionsOf(shape.kind, shape.fib);
      if (b && options) {
        if (options.trendLine) dashed(ctx, a, b);
        for (const entry of activeLevels(options)) {
          const target = { x: b.x, y: a.y + (b.y - a.y) * entry.value };
          ctx.strokeStyle = entry.color;
          ctx.fillStyle = entry.color;
          line(ctx, a, project(a, target, scope, 'forward'));
          label(ctx, target, entry.value.toFixed(3), entry.color, options.fontSize);
        }
      }
      break;
    }

    case 'fib-time': {
      const options = fibOptionsOf(shape.kind, shape.fib);
      if (b && options) {
        const stepPx = b.x - a.x;
        if (stepPx !== 0) {
          for (const entry of activeLevels(options)) {
            const x = a.x + stepPx * entry.value;
            if (x < 0 || x > scope.width) continue;
            ctx.strokeStyle = entry.color;
            ctx.fillStyle = entry.color;
            line(ctx, { x, y: 0 }, { x, y: scope.height });
            label(ctx, { x, y: 12 }, String(entry.value), entry.color, options.fontSize);
          }
        }
      }
      break;
    }

    // --- прогноз позиции --------------------------------------------------
    case 'long-position':
    case 'short-position':
      if (b && c) position(scope, shape, a, b, c);
      break;

    // --- фигуры -----------------------------------------------------------
    case 'rect':
      if (b) {
        fill(ctx, corners(a, b));
        ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
        const box = { left: Math.min(a.x, b.x), top: Math.min(a.y, b.y), right: Math.max(a.x, b.x), bottom: Math.max(a.y, b.y) };
        // Границы — чтобы поле ввода надписи встало внутри прямоугольника.
        remember(scope, shape, box);
        // Центр — для подсказки «+ Добавьте текст»; сам текст рисует rectText.
        caption(scope, shape, { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2, angle: 0, place: 'center' });
        // Средняя линия по желанию — цена середины зоны, своим цветом.
        const rectOptions = shapeOptionsOf(shape.options);
        if (rectOptions.midline) {
          ctx.save();
          ctx.setLineDash([6, 5]);
          ctx.strokeStyle = withAlpha(rectOptions.midlineColor, 0.9);
          ctx.lineWidth = 1;
          const mid = Math.round((box.top + box.bottom) / 2) + 0.5;
          ctx.beginPath();
          ctx.moveTo(box.left, mid);
          ctx.lineTo(box.right, mid);
          ctx.stroke();
          ctx.restore();
        }
        if (shape.text) rectText(ctx, box, shape);
      }
      break;

    case 'circle':
      if (b) {
        const radius = Math.hypot(b.x - a.x, b.y - a.y);
        ctx.beginPath();
        ctx.arc(a.x, a.y, radius, 0, Math.PI * 2);
        translucent(ctx, () => ctx.fill());
        ctx.stroke();
        caption(scope, shape, { x: a.x, y: a.y, angle: 0, place: 'center' });
      }
      break;

    case 'ellipse':
      if (b) {
        ctx.beginPath();
        ctx.ellipse(
          (a.x + b.x) / 2,
          (a.y + b.y) / 2,
          Math.abs(b.x - a.x) / 2,
          Math.abs(b.y - a.y) / 2,
          0,
          0,
          Math.PI * 2,
        );
        translucent(ctx, () => ctx.fill());
        ctx.stroke();
        caption(scope, shape, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, angle: 0, place: 'center' });
      }
      break;

    case 'triangle':
      if (b && c) {
        fill(ctx, [a, b, c]);
        polyline(ctx, [a, b, c, a]);
      }
      break;

    case 'polyline': {
      // Ломаная — прямые отрезки с мягкими стыками, без сглаживания.
      const all = points.filter((point): point is Screen => point !== null);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      polyline(ctx, all);
      const spot = spotOnLongest(all);
      if (spot) caption(scope, shape, spot);
      break;
    }

    case 'brush':
    case 'highlighter': {
      const all = points.filter((point): point is Screen => point !== null);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (shape.kind === 'highlighter') {
        ctx.globalAlpha = 0.35;
        ctx.lineWidth = shape.width * 6;
      }
      // Мазок — гладкой кривой через середины соседних точек, а не ломаной:
      // рука не рисует углов, которые даёт частота событий мыши.
      smoothStroke(ctx, all);
      ctx.globalAlpha = 1;
      break;
    }

    case 'path': {
      const all = points.filter((point): point is Screen => point !== null);
      polyline(ctx, all);
      const last = all[all.length - 1];
      const prev = all[all.length - 2];
      if (last && prev) arrowHead(ctx, prev, last);
      const spot = spotOnLongest(all);
      if (spot) caption(scope, shape, spot);
      break;
    }

    // --- стрелки ----------------------------------------------------------
    case 'arrow':
      if (b) {
        solidArrow(ctx, a, b, shape.width);
        remember(scope, shape, arrowLabel(ctx, shape, a, b, 'tail'));
      }
      break;

    case 'arrow-up':
    case 'arrow-down': {
      const tail = arrowTail(a, b, shape.kind === 'arrow-up' ? 'up' : 'down');
      solidArrow(ctx, tail, a, Math.max(shape.width, 2) * 2.2);
      remember(scope, shape, arrowLabel(ctx, shape, tail, a, 'behind'));
      break;
    }

    // --- текст и заметки --------------------------------------------------
    case 'text':
      remember(scope, shape, plainText(ctx, a, shape.text ?? '', shape));
      break;

    case 'note':
      remember(scope, shape, boxedText(ctx, a, shape.text ?? '', shape.color));
      break;

    case 'price-label':
      remember(scope, shape, boxedText(ctx, a, priceText(shape.points[0]?.price), shape.color));
      break;

    case 'callout':
      if (b) {
        line(ctx, a, b);
        remember(scope, shape, boxedText(ctx, b, shape.text ?? '', shape.color));
      }
      break;

    case 'flag':
      flag(ctx, a);
      remember(scope, shape, { left: a.x - 2, top: a.y - 20, right: a.x + 16, bottom: a.y + 2 });
      break;

    case 'icon': {
      // Наш стикер — картинкой, своего размера и наклона; за углы тянут, сверху крутят.
      const sticker = stickerOf(shape.text);
      if (sticker) {
        const pose = poseOf(shape);
        const image = stickerImage(sticker);
        if (image) {
          ctx.save();
          ctx.translate(a.x, a.y);
          ctx.rotate(pose.rotation);
          ctx.drawImage(image, -pose.size / 2, -pose.size / 2, pose.size, pose.size);
          ctx.restore();
        }
        scope.anchors?.set(shape.id, stickerHandles(a, pose));
        remember(scope, shape, stickerBounds(a, pose));
        break;
      }
      ctx.font = '18px system-ui, "Segoe UI Emoji", "Apple Color Emoji", sans-serif';
      ctx.fillText(shape.text ?? '⭐', a.x - 9, a.y);
      remember(scope, shape, { left: a.x - 11, top: a.y - 11, right: a.x + 11, bottom: a.y + 11 });
      break;
    }

    // --- диапазоны --------------------------------------------------------
    case 'price-range':
      if (b) {
        priceRange(scope, shape, a, b);
        // Линейка цены вертикальна: вторая опора — над первой.
        scope.anchors?.set(shape.id, [a, { x: a.x, y: b.y }]);
      }
      break;

    case 'date-range':
      if (b) {
        dateRange(scope, shape, a, b);
        // Линейка времени горизонтальна: вторая опора — на её уровне.
        scope.anchors?.set(shape.id, [a, { x: b.x, y: a.y }]);
      }
      break;

    case 'measure':
      if (b) measure(scope, shape, a, b);
      break;
  }

  ctx.restore();
}

// --- общие примитивы --------------------------------------------------------

function line(ctx: CanvasRenderingContext2D, from: Screen, to: Screen): void {
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
}

function dashed(ctx: CanvasRenderingContext2D, from: Screen, to: Screen): void {
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.globalAlpha = 0.6;
  line(ctx, from, to);
  ctx.restore();
}

/**
 * Гладкий мазок: квадратичные кривые через середины отрезков.
 *
 * Точки, ближе полутора пикселей к предыдущей, пропускаются — это дрожь
 * руки и частота событий мыши, а не рисунок.
 */
function smoothStroke(ctx: CanvasRenderingContext2D, raw: readonly Screen[]): void {
  const points: Screen[] = [];
  for (const point of raw) {
    const last = points[points.length - 1];
    if (!last || Math.hypot(point.x - last.x, point.y - last.y) >= 1.5) points.push(point);
  }
  const lastRaw = raw[raw.length - 1];
  if (lastRaw && points[points.length - 1] !== lastRaw) points.push(lastRaw);
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length - 1; i += 1) {
    const here = points[i]!;
    const next = points[i + 1]!;
    ctx.quadraticCurveTo(here.x, here.y, (here.x + next.x) / 2, (here.y + next.y) / 2);
  }
  const end = points[points.length - 1]!;
  ctx.lineTo(end.x, end.y);
  ctx.stroke();
}

function polyline(ctx: CanvasRenderingContext2D, points: readonly Screen[]): void {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.stroke();
}

function fill(ctx: CanvasRenderingContext2D, points: readonly Screen[]): void {
  if (points.length < 3) return;
  // Заливка — с переливом вдоль фигуры: у начала гуще, к концу бледнее.
  const xs = points.map((point) => point.x);
  const color = typeof ctx.fillStyle === 'string' ? ctx.fillStyle : null;
  translucent(ctx, () => {
    ctx.save();
    ctx.shadowBlur = 0;
    if (color && /^#[0-9a-f]{6}$/i.test(color)) {
      const gradient = ctx.createLinearGradient(Math.min(...xs), 0, Math.max(...xs), 0);
      gradient.addColorStop(0, withAlpha(color, 1));
      gradient.addColorStop(1, withAlpha(color, 0.45));
      ctx.fillStyle = gradient;
    }
    ctx.beginPath();
    ctx.moveTo(points[0]!.x, points[0]!.y);
    for (const point of points.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  });
}

/** Линии и каналы — со свечением. */
const GLOW_KINDS = new Set<string>([
  'trend',
  'ray',
  'extended',
  'info-line',
  'trend-angle',
  'horizontal',
  'horizontal-ray',
  'vertical',
  'cross',
  'parallel-channel',
  'flat-channel',
  'disjoint-channel',
  'regression',
]);

/** Заливка всегда полупрозрачна: под фигурой должна остаться видна цена. */
function translucent(ctx: CanvasRenderingContext2D, draw: () => void): void {
  const alpha = ctx.globalAlpha;
  ctx.globalAlpha = alpha * 0.12;
  draw();
  ctx.globalAlpha = alpha;
}

function corners(a: Screen, b: Screen): Screen[] {
  return [a, { x: b.x, y: a.y }, b, { x: a.x, y: b.y }];
}

function midpoint(a: Screen, b: Screen): Screen {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Продолжение отрезка до края холста. */
function project(a: Screen, b: Screen, scope: DrawScope, _mode: 'forward'): Screen {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx === 0) return { x: a.x, y: dy >= 0 ? scope.height : 0 };

  const edge = dx > 0 ? scope.width : 0;
  const t = (edge - a.x) / dx;
  return { x: edge, y: a.y + dy * t };
}

function label(
  ctx: CanvasRenderingContext2D,
  at: Screen,
  text: string,
  color: string,
  fontSize?: number,
): void {
  if (!text) return;
  if (fontSize) ctx.font = `500 ${fontSize}px Onest, system-ui, sans-serif`;
  const width = ctx.measureText(text).width + 14;
  const height = 17;
  // У края графика подпись сдвигается внутрь, а не обрезается.
  const left = onCanvasLeft(ctx, at.x + 6, width);
  const top = onCanvasTop(ctx, at.y - height / 2, height);
  /*
   * Пилюлей — как подписи уровней, позиций и паттернов: тёмное стекло,
   * кромка цвета фигуры. Голый прямоугольник под текстом выбивался из
   * общего вида и читался как системная подсказка.
   */
  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.roundRect(left, top, width, height, height / 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.88)';
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.7);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + 7, top + height / 2 + 0.5);
  ctx.restore();
}

function boxedText(
  ctx: CanvasRenderingContext2D,
  at: Screen,
  text: string,
  color: string,
): Rect {
  const width = ctx.measureText(text).width;
  ctx.save();
  ctx.fillStyle = 'rgba(13, 16, 22, 0.92)';
  ctx.fillRect(at.x, at.y - 10, width + 12, 20);
  ctx.strokeStyle = color;
  // Штриховку не сбрасываем: у заметки и сноски рамка должна слушаться
  // выбранного стиля линии, иначе настройка ничего не меняет.
  ctx.strokeRect(at.x, at.y - 10, width + 12, 20);
  ctx.fillStyle = color;
  ctx.fillText(text, at.x + 6, at.y);
  ctx.restore();

  return { left: at.x, top: at.y - 10, right: at.x + width + 12, bottom: at.y + 10 };
}

/**
 * Подпись пользователя.
 *
 * Многострочная: перенос по словам и по нажатым Enter. Фон и рамка
 * включаются настройкой — на пёстром участке графика надпись без
 * подложки не читается.
 */
function plainText(
  ctx: CanvasRenderingContext2D,
  at: Screen,
  text: string,
  shape: Drawing,
): Rect {
  const style = textStyleOf(shape.textStyle, shape.kind);
  ctx.save();
  ctx.font = fontOf(style);

  const box = textBox(at, text, style, (line) => ctx.measureText(line).width);

  // Скруглённая подложка и волосяная рамка — чётко, без размытых краёв.
  const x = Math.round(box.left) + 0.5;
  const y = Math.round(box.top) + 0.5;
  const w = Math.round(box.right - box.left);
  const h = Math.round(box.bottom - box.top);
  if (style.background) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 7);
    ctx.fillStyle = withAlpha(style.backgroundColor, style.backgroundOpacity);
    ctx.fill();
  }
  if (style.border) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 7);
    ctx.strokeStyle = withAlpha(shape.color, 0.75);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // Без подложки — лёгкая тень под буквами: на пёстром графике они читаются.
  if (!style.background) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
    ctx.shadowBlur = 4;
  }
  const empty = text.length === 0;
  ctx.fillStyle = empty ? withAlpha(shape.color, 0.45) : shape.color;
  ctx.textBaseline = 'middle';
  (empty ? ['Текст'] : box.lines).forEach((line, index) => {
    ctx.fillText(line, box.left + box.padding, at.y + index * box.lineHeight);
  });
  ctx.restore();

  return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

/**
 * Надпись внутри прямоугольника — по центру, с переносом по ширине.
 *
 * Прямоугольник часто отмечает зону, и подписать её хочется прямо в ней:
 * «Накопление», «Зона интереса». Текст не вылезает за рамку — лишнее
 * обрезается по её краю.
 */
function rectText(ctx: CanvasRenderingContext2D, box: Rect, shape: Drawing): void {
  const style = textStyleOf(shape.textStyle, shape.kind);
  const width = box.right - box.left;
  const height = box.bottom - box.top;
  if (width < 16 || height < 10) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.left + 1, box.top + 1, width - 2, height - 2);
  ctx.clip();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  ctx.font = fontOf(style);
  const lines = wrapText(shape.text ?? '', { ...style, wrap: true, wrapWidth: Math.max(20, width - 20) }, (line) => ctx.measureText(line).width);
  const lineHeight = Math.round(style.fontSize * 1.4);
  const block = lines.length * lineHeight;
  // Со средней линией надпись встаёт в верхнюю половину, если там хватает места:
  // пунктир не должен перечёркивать буквы.
  const midline = shapeOptionsOf(shape.options).midline;
  const center = midline && height / 2 >= block + 6 ? box.top + height / 4 : box.top + height / 2;
  const top = center - ((lines.length - 1) * lineHeight) / 2;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  lines.forEach((line, index) => {
    const x = box.left + width / 2;
    const y = top + index * lineHeight;
    // Тёмная обводка букв: надпись читается и поверх свечей, и поверх пунктира.
    ctx.lineWidth = 3.5;
    ctx.strokeStyle = 'rgba(7, 11, 22, 0.85)';
    ctx.strokeText(line, x, y);
    ctx.fillStyle = style.color ?? shape.color;
    ctx.fillText(line, x, y);
  });
  ctx.restore();
}

/** Отметить, где подписывать линию: надпись нарисует слой. */
function caption(scope: DrawScope, shape: Drawing, spot: CaptionSpot): void {
  scope.captions?.set(shape.id, spot);
}

/** Запомнить занятое фигурой место — по нему считается попадание курсором. */
function remember(scope: DrawScope, shape: Drawing, rect: Rect): void {
  scope.bounds?.set(shape.id, rect);
}

function arrowHead(ctx: CanvasRenderingContext2D, from: Screen, to: Screen): void {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const size = 9;
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - size * Math.cos(angle - Math.PI / 7), to.y - size * Math.sin(angle - Math.PI / 7));
  ctx.lineTo(to.x - size * Math.cos(angle + Math.PI / 7), to.y - size * Math.sin(angle + Math.PI / 7));
  ctx.closePath();
  ctx.fill();
}

/**
 * Хвост вертикальной стрелки.
 *
 * Остриё стоит в первой точке — там, куда указывают, — а хвост тянется
 * от неё в противоположную сторону на длину протяжки. Ось экрана растёт
 * вниз, поэтому «вверх» это меньшая координата.
 */
export function arrowTail(
  tip: Screen,
  drag: Screen | null,
  direction: 'up' | 'down',
): Screen {
  const dragged = drag ? Math.abs(drag.y - tip.y) : 0;
  const length = Math.max(28, dragged);
  return { x: tip.x, y: direction === 'up' ? tip.y + length : tip.y - length };
}

/**
 * Сплошная стрелка от хвоста к острию.
 *
 * Не линия с галочкой на конце, а закрашенная фигура: тонкая стрелка
 * теряется на свечах, а указатель должен быть виден сразу. Толщина
 * растёт вместе с настройкой ширины, голова — вместе с древком, так
 * что пропорции держатся на любом размере.
 */
function solidArrow(
  ctx: CanvasRenderingContext2D,
  from: Screen,
  to: Screen,
  width: number,
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1) return;

  const ux = dx / length;
  const uy = dy / length;
  // Нормаль к оси стрелки: по ней откладывается ширина.
  const nx = -uy;
  const ny = ux;

  const shaft = Math.max(3, width * 2.4);
  const headWidth = shaft * 2.6;
  // Голова не длиннее трети стрелки: иначе у короткой не остаётся древка.
  const headLength = Math.min(length * 0.34, headWidth * 1.4);
  const baseX = to.x - ux * headLength;
  const baseY = to.y - uy * headLength;

  const at = (x: number, y: number, offset: number): Screen => ({
    x: x + nx * offset,
    y: y + ny * offset,
  });

  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  const outline = [
    at(from.x, from.y, shaft / 2),
    at(baseX, baseY, shaft / 2),
    at(baseX, baseY, headWidth / 2),
    to,
    at(baseX, baseY, -headWidth / 2),
    at(baseX, baseY, -shaft / 2),
    at(from.x, from.y, -shaft / 2),
  ];
  ctx.moveTo(outline[0]!.x, outline[0]!.y);
  for (const point of outline.slice(1)) ctx.lineTo(point.x, point.y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Подпись у стрелки.
 *
 * Стоит со стороны хвоста, чтобы не закрывать то, на что указывают:
 * у вертикальной — под ней или над ней, у наклонной — за хвостом.
 */
function arrowLabel(
  ctx: CanvasRenderingContext2D,
  shape: Drawing,
  tail: Screen,
  tip: Screen,
  place: 'tail' | 'behind',
): Rect {
  const text = shape.text ?? '';
  if (!text) return { left: tip.x - 6, top: tip.y - 6, right: tip.x + 6, bottom: tip.y + 6 };

  const style = textStyleOf(shape.textStyle, shape.kind);
  ctx.save();
  ctx.font = fontOf(style);
  ctx.textBaseline = 'middle';
  ctx.textAlign = place === 'behind' ? 'center' : 'right';

  const width = ctx.measureText(text).width;
  const gap = 8;
  // У вертикальной стрелки подпись уходит дальше от острия, у наклонной
  // ставится вплотную к хвосту.
  const x = place === 'behind' ? tail.x : tail.x - gap;
  const y = place === 'behind' ? tail.y + Math.sign(tail.y - tip.y) * 14 : tail.y;

  if (style.background) {
    ctx.globalAlpha = style.backgroundOpacity;
    ctx.fillStyle = style.backgroundColor;
    const left = place === 'behind' ? x - width / 2 - 4 : x - width - 4;
    ctx.fillRect(left, y - style.fontSize, width + 8, style.fontSize * 2);
    ctx.globalAlpha = 1;
  }

  ctx.fillStyle = shape.color;
  ctx.fillText(text, x, y);
  ctx.restore();

  const left = place === 'behind' ? x - width / 2 - 4 : x - width - 4;
  return { left, top: y - style.fontSize, right: left + width + 8, bottom: y + style.fontSize };
}

function flag(ctx: CanvasRenderingContext2D, at: Screen): void {
  line(ctx, at, { x: at.x, y: at.y - 18 });
  ctx.beginPath();
  ctx.moveTo(at.x, at.y - 18);
  ctx.lineTo(at.x + 14, at.y - 14);
  ctx.lineTo(at.x, at.y - 10);
  ctx.closePath();
  ctx.fill();
}

// --- расчётные фигуры -------------------------------------------------------

/**
 * Сетка уровней между двумя ценами.
 *
 * Считается по ценам, а не по пикселям: на логарифмической шкале
 * середина экрана и середина хода — разные вещи, и уровень 0.5 обязан
 * стоять на половине движения цены.
 */
function fibLevels(
  scope: DrawScope,
  shape: Drawing,
  options: FibOptions,
  a: Screen,
  b: Screen,
  priceFrom: number,
  priceTo: number,
): void {
  const { ctx } = scope;
  // Откуда считать: у новых сеток 0 — у второй точки; переворот меняет местами.
  const [from, to] = fibSpan(options, priceFrom, priceTo);

  const left = extendLeft(options) ? 0 : Math.min(a.x, b.x);
  const right = extendRight(options) ? scope.width : Math.max(a.x, b.x);

  const levels = activeLevels(options);
  const rows = levels
    .map((entry) => {
      const price = from + (to - from) * entry.value;
      const point = scope.screen({ time: shape.points[0]!.time, price });
      return point ? { entry, price, y: point.y } : null;
    })
    .filter((row): row is { entry: (typeof levels)[number]; price: number; y: number } => row !== null);

  /*
   * Заливка — полосами между соседними уровнями, с переливом по ширине:
   * у начала гуще, к концу бледнее. Так видно, в какой зоне цена, и
   * полосы не лежат плоскими плашками поверх свечей.
   */
  if (options.background) {
    for (let i = 1; i < rows.length; i += 1) {
      const upper = rows[i - 1]!;
      const lower = rows[i]!;
      const gradient = ctx.createLinearGradient(left, 0, right, 0);
      gradient.addColorStop(0, withAlpha(lower.entry.color, options.backgroundOpacity * 1.4));
      gradient.addColorStop(1, withAlpha(lower.entry.color, options.backgroundOpacity * 0.45));
      ctx.save();
      ctx.fillStyle = gradient;
      ctx.fillRect(left, Math.min(upper.y, lower.y), right - left, Math.abs(lower.y - upper.y));
      ctx.restore();
    }
  }

  /*
   * Золотая зона — между 0.618 и 0.786: там откат чаще всего и
   * заканчивается. Золотом, с пунктирной рамкой и подписью: это место,
   * ради которого инструмент и тянут.
   */
  const golden = rows.find((row) => Math.abs(row.entry.value - 0.618) < 1e-6);
  const deep = rows.find((row) => Math.abs(row.entry.value - 0.786) < 1e-6);
  if (golden && deep) {
    const top = Math.min(golden.y, deep.y);
    const height = Math.abs(golden.y - deep.y);
    ctx.save();
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(245, 192, 78, 0.13)';
    ctx.fillRect(left, top, right - left, height);
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = 'rgba(245, 192, 78, 0.55)';
    ctx.lineWidth = 1;
    ctx.strokeRect(left + 0.5, top + 0.5, right - left - 1, height - 1);
    ctx.restore();
    if (height > 14) levelPill(ctx, { x: (left + right) / 2, y: top + height / 2 }, 'золотая зона', '#F5C04E', 'center', options.fontSize);
  }

  for (const row of rows) {
    // Ключевые уровни — толще, промежуточные — тоньше и прозрачнее.
    const key = [0, 0.5, 0.618, 1].some((value) => Math.abs(row.entry.value - value) < 1e-6);
    ctx.save();
    ctx.strokeStyle = row.entry.color;
    ctx.globalAlpha *= key ? 1 : 0.7;
    ctx.lineWidth = key ? Math.max(shape.width, 1.5) : Math.max(1, shape.width - 0.5);
    line(ctx, { x: left, y: row.y }, { x: right, y: row.y });
    ctx.restore();

    const text = levelLabel(options, row.entry.value, row.price, priceText);
    if (!text) continue;
    if (options.labelSide === 'left') levelPill(ctx, { x: left - 6, y: row.y }, text, row.entry.color, 'right', options.fontSize);
    else levelPill(ctx, { x: right + 6, y: row.y }, text, row.entry.color, 'left', options.fontSize);
  }
}

/** Крупная пилюля соотношения: перелив от цвета риска к цвету цели. */
function ratioBadge(ctx: CanvasRenderingContext2D, at: Screen, text: string, good: string, bad: string): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '700 12.5px Onest, system-ui, sans-serif';
  const width = ctx.measureText(text).width + 18;
  const height = 22;
  const gradient = ctx.createLinearGradient(at.x, 0, at.x + width, 0);
  gradient.addColorStop(0, withAlpha(bad, 0.9));
  gradient.addColorStop(1, withAlpha(good, 0.9));
  ctx.beginPath();
  ctx.roundRect(at.x, at.y - height / 2, width, height, height / 2);
  ctx.shadowColor = withAlpha(good, 0.4);
  ctx.shadowBlur = 10;
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#060B15';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, at.x + 9, at.y + 0.5);
  ctx.restore();
}

/** Подпись уровня — пилюля: тёмная подложка, кромка и текст цвета уровня. */
function levelPill(
  ctx: CanvasRenderingContext2D,
  at: Screen,
  text: string,
  color: string,
  align: 'left' | 'right' | 'center',
  fontSize: number,
): void {
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = `600 ${Math.max(9, fontSize - 0.5)}px Onest, system-ui, sans-serif`;
  const width = ctx.measureText(text).width + 12;
  const height = Math.max(15, fontSize + 5);
  const left = align === 'left' ? at.x : align === 'right' ? at.x - width : at.x - width / 2;
  ctx.beginPath();
  ctx.roundRect(left, at.y - height / 2, width, height, height / 2);
  ctx.fillStyle = 'rgba(7, 11, 22, 0.9)';
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.75);
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + 6, at.y + 0.5);
  ctx.restore();
}


function extendLeft(options: FibOptions): boolean {
  return options.extend === 'left' || options.extend === 'both';
}

function extendRight(options: FibOptions): boolean {
  return options.extend === 'right' || options.extend === 'both';
}

function labelWidth(ctx: CanvasRenderingContext2D, text: string): number {
  return ctx.measureText(text).width + 16;
}

/**
 * Регрессионный тренд — канал ANZONIX.
 *
 * Как в привычных терминалах: тянут линию — видна линия; отпустили —
 * по закрытиям свечей на отрезке строится прямая наименьших квадратов и
 * канал в заданное число стандартных отклонений. Тянут конец средней
 * линии — снова видна линия, после отпускания канал пересчитывается.
 *
 * Свой облик: верхняя половина — цветом «дорого», нижняя — «дёшево»
 * (по умолчанию красная сверху и зелёная снизу), переливом от средней
 * линии к краю; средняя — пунктиром; сила тренда подписана словами.
 */
function regression(scope: DrawScope, shape: Drawing, a: Screen, b: Screen): void {
  const { ctx } = scope;
  const from = shape.points[0]?.time ?? 0;
  const to = shape.points[1]?.time ?? 0;

  // Пока тянут — просто линия: канал появится, когда отпустят.
  if (scope.stretchingId === shape.id) {
    line(ctx, a, b);
    caption(scope, shape, spotOn(a, b));
    return;
  }

  const fit = regressionFit(scope.candles, from, to);
  if (!fit) {
    line(ctx, a, b);
    caption(scope, shape, spotOn(a, b));
    return;
  }
  const options = shapeOptionsOf(shape.options);
  const band = fit.deviation * options.deviation;
  const at = (time: number, price: number): Screen | null => scope.screen({ time, price });
  const left = at(fit.first.time, fit.start);
  const right = at(fit.last.time, fit.end);
  const upLeft = at(fit.first.time, fit.start + band);
  const upRight = at(fit.last.time, fit.end + band);
  const downLeft = at(fit.first.time, fit.start - band);
  const downRight = at(fit.last.time, fit.end - band);
  if (!left || !right || !upLeft || !upRight || !downLeft || !downRight) return;
  // Опоры — на концах средней линии, в порядке точек фигуры.
  scope.anchors?.set(shape.id, from <= to ? [left, right] : [right, left]);
  // Подпись — над верхним краем канала.
  const topEdge = (upLeft.y + upRight.y) / 2 <= (downLeft.y + downRight.y) / 2 ? [upLeft, upRight] : [downLeft, downRight];
  caption(scope, shape, spotOn(topEdge[0]!, topEdge[1]!));

  ctx.save();
  ctx.setLineDash([]);
  ctx.shadowBlur = 0;
  const half = (edgeL: Screen, edgeR: Screen, color: string): void => {
    ctx.beginPath();
    ctx.moveTo(left.x, left.y);
    ctx.lineTo(right.x, right.y);
    ctx.lineTo(edgeR.x, edgeR.y);
    ctx.lineTo(edgeL.x, edgeL.y);
    ctx.closePath();
    // Перелив от средней линии к краю: у края гуще — там цена экстремальна.
    const midY = (left.y + right.y) / 2;
    const edgeY = (edgeL.y + edgeR.y) / 2;
    const glow = ctx.createLinearGradient(0, midY, 0, edgeY);
    glow.addColorStop(0, withAlpha(color, 0.04));
    glow.addColorStop(1, withAlpha(color, 0.2));
    ctx.fillStyle = glow;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(edgeL.x, edgeL.y);
    ctx.lineTo(edgeR.x, edgeR.y);
    ctx.strokeStyle = withAlpha(color, 0.9);
    ctx.lineWidth = Math.max(1.2, shape.width);
    ctx.stroke();
  };
  half(upLeft, upRight, options.upperColor);
  half(downLeft, downRight, options.lowerColor);
  ctx.restore();

  // Средняя линия — пунктиром, цветом фигуры.
  ctx.save();
  ctx.setLineDash([6, 5]);
  ctx.strokeStyle = withAlpha(shape.color, 0.9);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(right.x, right.y);
  ctx.stroke();
  ctx.restore();

  // Сила тренда — словами, у нижнего края слева.
  if (options.showR) {
    const r = fit.r;
    label(ctx, { x: downLeft.x - 6, y: downLeft.y + 14 }, `R ${r.toFixed(2).replace('.', ',')} · ${trendWords(r)}`, options.lowerColor);
  }
}

/**
 * Прогноз позиции.
 *
 * Зелёная зона — от входа до цели, красная — до стопа. Подписи несут
 * то, ради чего инструмент рисуют: объём позиции, деньги в риске,
 * прибыль на цели и отношение одного к другому.
 *
 * По умолчанию подписи всплывают под курсором: три плашки на каждом
 * боксе превратили бы график в таблицу. Настройкой их можно закрепить.
 */
function position(scope: DrawScope, shape: Drawing, a: Screen, b: Screen, c: Screen): void {
  const { ctx } = scope;
  const options = positionOptionsOf(shape.position);

  const result = computePosition(
    shape.kind,
    shape.points[0]!.price,
    shape.points[1]!.price,
    shape.points[2]!.price,
    options,
    scope.tickSize,
  );

  const left = Math.min(a.x, c.x, b.x);
  const right = Math.max(a.x, c.x, b.x, left + 60);

  // Зоны: убыток от входа до стопа, прибыль от входа до цели.
  zone(ctx, left, right, a.y, b.y, options.stopColor);
  zone(ctx, left, right, a.y, c.y, options.targetColor);

  ctx.strokeStyle = options.lineColor;
  ctx.fillStyle = options.lineColor;
  line(ctx, { x: left, y: a.y }, { x: right, y: a.y });

  /*
   * Частичные цели — пунктиром между входом и главной целью, с подписью
   * «TP1 · цена · 1R». Подпись видна всегда: это и есть план выхода,
   * ради которого цели заводили.
   */
  const partial = options.targets > 1 ? options.targets : 0;
  for (let step = 1; step < partial; step += 1) {
    const share = step / partial;
    const y = a.y + (c.y - a.y) * share;
    const price = result.entry + (result.target - result.entry) * share;
    const r = result.riskPerUnit > 0 ? (Math.abs(price - result.entry) / result.riskPerUnit).toFixed(1) : '—';
    ctx.save();
    ctx.strokeStyle = options.targetColor;
    ctx.setLineDash([5, 4]);
    line(ctx, { x: left, y }, { x: right, y });
    ctx.restore();
    levelPill(ctx, { x: right + 6, y }, `TP${step} · ${priceText(price)} · ${r}R`, options.targetColor, 'left', options.fontSize);
  }
  if (partial > 0) {
    const r = result.ratio === null ? '—' : result.ratio.toFixed(1);
    levelPill(ctx, { x: right + 6, y: c.y }, `TP${partial} · ${r}R`, options.targetColor, 'left', options.fontSize);
  }

  if (!showsNumbers(options, shape.id, scope.hoveredId, scope.activeId)) return;

  const size = options.fontSize;
  const qty = quantityText(result.quantity, options, scope.qtyPrecision);

  /*
   * Подписи — под линиями, а не на них.
   *
   * `label` рисует плашку по центру точки: половина её ложилась ровно на
   * линию цели, входа или стопа и закрывала собой то, что человек
   * тянет. При растягивании позиции граница пропадала под собственной
   * подписью, и приходилось целиться вслепую.
   *
   * Одиннадцать пикселей — высота половины плашки плюс просвет: линия
   * остаётся целиком видна, а подпись читается как относящаяся к ней, а
   * не к соседней.
   */
  const drop = 11;

  if (options.priceLabels) {
    // Подписи — внутри своей зоны, у её границы: пилюлями цвета зоны.
    const inward = (edge: number): number => (edge < a.y ? edge + drop : edge - drop);
    levelPill(
      ctx,
      { x: left + 6, y: inward(c.y) },
      `Цель ${priceText(result.target)} · ${signed(result.targetPercent)}% · +${money(result.profit, options.compact)}`,
      options.targetColor,
      'left',
      size,
    );
    levelPill(
      ctx,
      { x: left + 6, y: inward(b.y) },
      `Стоп ${priceText(result.stop)} · ${signed(result.stopPercent)}% · −${money(result.loss, options.compact)}`,
      options.stopColor,
      'left',
      size,
    );
  }

  // Соотношение прибыли к риску — крупно посреди зоны прибыли: это главное число сделки.
  if (result.ratio !== null && Math.abs(c.y - a.y) > 26) {
    const text = `1 : ${result.ratio.toFixed(2)}`;
    ctx.save();
    ctx.font = '700 12.5px Onest, system-ui, sans-serif';
    const width = ctx.measureText(text).width + 18;
    ctx.restore();
    ratioBadge(ctx, { x: (left + right) / 2 - width / 2, y: (a.y + c.y) / 2 }, text, options.targetColor, options.stopColor);
  }

  if (options.info === 'none') return;

  const ratio = result.ratio === null ? '—' : result.ratio.toFixed(2);
  // Соотношение не повторяется: оно крупно в зоне прибыли.
  void ratio;
  const head = `Вход ${priceText(result.entry)} · объём ${qty}`;
  const tail = result.cappedByLeverage
    ? ' · объём ограничен плечом'
    : ` · в риске ${money(result.riskMoney, options.compact)}`;

  levelPill(ctx, { x: left + 6, y: a.y + (c.y < a.y ? drop : -drop) }, options.info === 'short' ? head : head + tail, '#C9D4E5', 'left', size);
}

/** Полупрозрачная зона между двумя ценами. */
function zone(
  ctx: CanvasRenderingContext2D,
  left: number,
  right: number,
  from: number,
  to: number,
  color: string,
): void {
  // Зона гуще у своей границы и прозрачнее у входа: видно, куда идёт сделка.
  const gradient = ctx.createLinearGradient(0, from, 0, to);
  gradient.addColorStop(0, withAlpha(color, 0.05));
  gradient.addColorStop(1, withAlpha(color, 0.3));
  ctx.save();
  ctx.fillStyle = gradient;
  ctx.fillRect(left, Math.min(from, to), right - left, Math.abs(to - from));
  ctx.restore();

  ctx.save();
  ctx.strokeStyle = color;
  ctx.shadowColor = withAlpha(color, 0.5);
  ctx.shadowBlur = 6;
  ctx.lineWidth = 1.5;
  line(ctx, { x: left, y: to }, { x: right, y: to });
  ctx.restore();
}

function signed(value: number): string {
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}`;
}

function priceRange(scope: DrawScope, shape: Drawing, a: Screen, b: Screen): void {
  const { ctx } = scope;
  const from = shape.points[0]?.price ?? 0;
  const to = shape.points[1]?.price ?? 0;
  const delta = to - from;
  const percent = from === 0 ? 0 : (delta / from) * 100;

  dashed(ctx, { x: a.x, y: a.y }, { x: a.x, y: b.y });
  line(ctx, { x: a.x - 6, y: a.y }, { x: a.x + 6, y: a.y });
  line(ctx, { x: a.x - 6, y: b.y }, { x: a.x + 6, y: b.y });
  label(
    ctx,
    { x: a.x, y: (a.y + b.y) / 2 },
    `${delta >= 0 ? '+' : ''}${priceText(delta)} (${percent.toFixed(2)}%)`,
    shape.color,
  );
}

function dateRange(scope: DrawScope, shape: Drawing, a: Screen, b: Screen): void {
  const { ctx } = scope;
  const bars = Math.abs(
    Math.round(scope.logical(shape.points[1]?.time ?? 0) - scope.logical(shape.points[0]?.time ?? 0)),
  );

  dashed(ctx, { x: a.x, y: a.y }, { x: b.x, y: a.y });
  line(ctx, { x: a.x, y: a.y - 6 }, { x: a.x, y: a.y + 6 });
  line(ctx, { x: b.x, y: a.y - 6 }, { x: b.x, y: a.y + 6 });
  label(ctx, { x: (a.x + b.x) / 2, y: a.y }, `${bars} баров`, shape.color);
}

/** Подпись линии данных и линейки: цена, проценты и число баров. */
function infoText(scope: DrawScope, shape: Drawing): string {
  const first = shape.points[0];
  const second = shape.points[1];
  if (!first || !second) return '';

  const delta = second.price - first.price;
  const percent = first.price === 0 ? 0 : (delta / first.price) * 100;
  const bars = Math.abs(Math.round(scope.logical(second.time) - scope.logical(first.time)));

  return `${delta >= 0 ? '+' : ''}${priceText(delta)} (${percent.toFixed(2)}%) · ${bars} баров`;
}

/** Цена в подписи: точность по величине, как и везде в терминале. */
export function priceText(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 8;
  return value.toFixed(digits);
}

/** Штриховка по выбранному стилю линии. */
export function dashOf(shape: Drawing): number[] {
  const style = LINE_STYLES.find((entry) => entry.id === shape.style);
  if (!style || style.dash.length === 0) return [];
  // Пунктир крупнее у толстых линий, иначе на 4px он сливается.
  return style.dash.map((value) => value * Math.max(1, shape.width * 0.7));
}

/**
 * Линейка.
 *
 * Отвечает на один вопрос: сколько прошла цена и за сколько времени.
 * Поэтому здесь не рамка с подписью сбоку, а закрашенная область со
 * стрелками по обеим осям и сводка под ней: цена, проценты, тики,
 * число баров, длительность и объём за отрезок.
 *
 * Цвет — по направлению движения: зелёный вверх, красный вниз. Это тот
 * же язык, что у свечей и у ленты сигналов; заводить для линейки
 * отдельный синий значило бы просить человека помнить ещё одну
 * раскраску.
 */
function measure(scope: DrawScope, shape: Drawing, a: Screen, b: Screen): void {
  const { ctx } = scope;
  const from = shape.points[0]!;
  const to = shape.points[1]!;

  const delta = to.price - from.price;
  const up = delta >= 0;
  const color = up ? '#2EE6B0' : '#FF5470';

  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);

  ctx.save();
  ctx.setLineDash([]);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;

  ctx.globalAlpha = 0.15;
  ctx.fillRect(left, top, right - left, bottom - top);
  ctx.globalAlpha = 1;

  // Стрелки по обеим осям: они и показывают, что именно измеряется.
  const midY = (top + bottom) / 2;
  const midX = (left + right) / 2;
  arrowLine(ctx, { x: left, y: midY }, { x: right, y: midY });
  arrowLine(ctx, { x: midX, y: a.y }, { x: midX, y: b.y });

  const percent = from.price === 0 ? 0 : (delta / from.price) * 100;
  const ticks = scope.tickSize > 0 ? Math.round(delta / scope.tickSize) : 0;
  const bars = Math.round(scope.logical(to.time) - scope.logical(from.time));
  const volume = volumeBetween(scope.candles, from.time, to.time);

  const lines = [
    `${signedPrice(delta)} (${signed(percent)}%) ${signedInt(ticks)}`,
    `Бары: ${Math.abs(bars)}, ${duration(Math.abs(to.time - from.time))}`,
    `Объём ${compactNumber(volume)}`,
  ];

  // Сводка встаёт с той стороны, куда тянули: она не должна закрывать
  // только что измеренный ход.
  plaque(ctx, midX, up ? top - 8 : bottom + 8, lines, color, up ? 'above' : 'below');
  ctx.restore();
}

/** Линия со стрелками на обоих концах. */
function arrowLine(ctx: CanvasRenderingContext2D, from: Screen, to: Screen): void {
  line(ctx, from, to);
  if (Math.hypot(to.x - from.x, to.y - from.y) < 12) return;
  arrowHead(ctx, from, to);
  arrowHead(ctx, to, from);
}

/** Многострочная плашка со сводкой. */
function plaque(
  ctx: CanvasRenderingContext2D,
  centreX: number,
  edgeY: number,
  lines: readonly string[],
  color: string,
  side: 'above' | 'below',
): void {
  /*
   * Стеклянная карточка, а не сплошной цветной блок.
   *
   * Зелёный прямоугольник с чёрным текстом — вид чужой линейки, в
   * которой любой узнал бы другую платформу. Здесь — наше стекло:
   * тёмная подложка, кромка цвета хода, первая строка — сам ход, крупно
   * и цветом, остальные — спокойно.
   */
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '600 12px Onest, system-ui, sans-serif';
  const headWidth = ctx.measureText(lines[0] ?? '').width;
  ctx.font = '500 11px Onest, system-ui, sans-serif';
  const restWidth = Math.max(0, ...lines.slice(1).map((text) => ctx.measureText(text).width));
  const lineHeight = 16;
  const padX = 12;
  const width = Math.max(headWidth, restWidth) + padX * 2;
  const height = lines.length * lineHeight + 12;
  const x = onCanvasLeft(ctx, centreX - width / 2, width);
  const y = onCanvasTop(ctx, side === 'above' ? edgeY - height : edgeY, height);

  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowBlur = 14;
  ctx.shadowOffsetY = 4;
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, 10);
  const fill = ctx.createLinearGradient(x, y, x, y + height);
  fill.addColorStop(0, 'rgba(18, 24, 44, 0.96)');
  fill.addColorStop(1, 'rgba(9, 13, 26, 0.96)');
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = withAlpha(color, 0.65);
  ctx.lineWidth = 1;
  ctx.stroke();
  // Цветной отлив сверху — как у остальных карточек разметки.
  const glow = ctx.createLinearGradient(x, y, x, y + height);
  glow.addColorStop(0, withAlpha(color, 0.16));
  glow.addColorStop(1, withAlpha(color, 0));
  ctx.fillStyle = glow;
  ctx.fill();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  lines.forEach((text, index) => {
    const head = index === 0;
    ctx.font = head ? '600 12px Onest, system-ui, sans-serif' : '500 11px Onest, system-ui, sans-serif';
    ctx.fillStyle = head ? color : '#C9D4E5';
    ctx.fillText(text, x + width / 2, y + 6 + lineHeight * (index + 0.5));
  });
  ctx.restore();
}

/** Объём за отрезок: сумма по барам, попавшим между точками. */
function volumeBetween(candles: readonly Candle[], from: number, to: number): number {
  const [start, end] = from <= to ? [from, to] : [to, from];
  let total = 0;
  for (const candle of candles) {
    if (candle.time >= start && candle.time <= end) total += candle.volume;
  }
  return total;
}

/** Длительность отрезка словами: 45м, 19ч, 3д 4ч. */
function duration(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}м`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}ч`;

  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest === 0 ? `${days}д` : `${days}д ${rest}ч`;
}

function signedPrice(value: number): string {
  return `${value >= 0 ? '+' : '−'}${priceText(Math.abs(value))}`;
}

function signedInt(value: number): string {
  return `${value >= 0 ? '+' : '−'}${Math.abs(value)}`;
}

function compactNumber(value: number): string {
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}

/**
 * Набросок незаконченной фигуры.
 *
 * Показывает то, что человек уже задал: отрезок между поставленными
 * точками и сами точки. Рисовать здесь настоящую фигуру нельзя — она
 * ещё не определена, — но и не показывать ничего значит оставить
 * человека без обратной связи посреди построения.
 */
function sketch(ctx: CanvasRenderingContext2D, points: ReadonlyArray<Screen | null>): void {
  const known = points.filter((point): point is Screen => point !== null);
  if (known.length === 0) return;

  ctx.save();
  ctx.globalAlpha = 0.8;
  polyline(ctx, known);
  for (const point of known) {
    ctx.beginPath();
    ctx.arc(point.x, point.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
